import React, { useEffect, useRef, useState } from 'react';
import ContentEditable from '../utils/SafeContentEditable';
import { createBlock } from '../utils/blockUtils';
import { focusEditable } from '../utils/focusUtils';
import { applyInlineMarkdown, handleEditablePaste } from '../utils/pasteUtils';
import {
  getCaretSplit,
  isCaretAtStart,
  isCaretAtEnd,
  isCaretOnFirstLine,
  isCaretOnLastLine,
  stripHtml,
} from '../utils/caretUtils';
import { consumeListStartBackspace, handleIndentKeyDown, indentStyle, inheritIndent, shouldHandleLineStartBackspace } from '../utils/indentUtils';
import { useMathPills } from '../hooks/useMathPills';
import { useVariableRefresh } from '../hooks/useVariableRefresh';
import { useVariablePicker } from '../hooks/useVariablePicker';
import { tryDeleteAdjacentAtomic } from '../utils/atomicCaret';

/**
 * NumberBlock — a numbered list item.
 *
 * Display index comes from siblings (indent-aware); not stored on the block.
 * Enter / empty-Enter / Backspace (outdent → drop marker → paragraph)
 * mirrors BulletBlock.
 * Delete at end merges the next block up (same as paragraphs).
 */
export const NumberBlock = ({
  block,
  listNumber = 1,
  onUpdate,
  onInsertAfter,
  onUpdateAndInsert,
  onNavigate,
  onMergeWithPrevious,
  onMergeWithNext,
  resolveVariable,
  availableVariables,
  onCreateVariable,
}) => {
  const [content, setContent] = useState(block.content || '');
  const contentEditableRef = useRef(null);
  const contentRef = useRef(content);

  useEffect(() => {
    if ((block.content || '') !== contentRef.current) {
      contentRef.current = block.content || '';
      setContent(block.content || '');
    }
  }, [block.content]);

  useVariableRefresh({
    block,
    contentRef,
    setContent,
    onUpdate,
    resolveVariable,
    contentEditableRef,
  });

  const { tryOpenFromBraceKey, variablePicker } = useVariablePicker({
    blockId: block.id,
    contentEditableRef,
    contentRef,
    setContent,
    onUpdate,
    resolveVariable,
    availableVariables,
    onCreateVariable,
  });

  const { mathMenu } = useMathPills({
    blockId: block.id,
    contentEditableRef,
    contentRef,
    setContent,
    onUpdate,
    resolveVariable,
    availableVariables,
    onCreateVariable,
  });

  const handleChange = (e) => {
    const value = applyInlineMarkdown(e.target.value);
    const typed = stripHtml(value).replace(/\u00a0/g, ' ');
    const prevTyped = stripHtml(contentRef.current).replace(/\u00a0/g, ' ');

    if (/^\[\s?\]/.test(typed) && !/^\[\s?\]/.test(prevTyped)) {
      const remainder = value.replace(/^\[(\s|&nbsp;|\u00a0)?\]/, '');
      onUpdate(block.id, {
        type: 'checkbox',
        content: remainder,
        props: { checked: false, ...inheritIndent(block) },
      });
      focusEditable(block.id, { atStart: true });
      return;
    }

    if (/^[-*] /.test(typed) && !/^[-*] /.test(prevTyped)) {
      const remainder = value.replace(/^[-*](&nbsp;|\u00a0| )/, '');
      onUpdate(block.id, {
        type: 'bullet',
        content: remainder,
        props: inheritIndent(block),
      });
      focusEditable(block.id, { atStart: true });
      return;
    }

    contentRef.current = value;
    setContent(value);
    onUpdate(block.id, { content: value });
  };

  const handleKeyDown = (e) => {
    const el = contentEditableRef.current;
    const isEmpty = stripHtml(contentRef.current).trim() === '';

    if (handleIndentKeyDown(e, block, onUpdate, el)) return;
    if (tryOpenFromBraceKey(e)) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isEmpty) {
        onUpdate(block.id, { type: 'text', content: '', props: {} });
        focusEditable(block.id);
        return;
      }
      const split = el ? getCaretSplit(el) : null;
      if (split && stripHtml(split.after).length > 0) {
        contentRef.current = split.before;
        setContent(split.before);
        onUpdateAndInsert(
          block.id,
          { content: split.before },
          createBlock('number', split.after, inheritIndent(block)),
          { focusAtStart: true }
        );
      } else {
        onInsertAfter?.(block.id, createBlock('number', '', inheritIndent(block)));
      }
      return;
    }

    if (e.key === 'ArrowUp' && el && isCaretOnFirstLine(el)) {
      if (onNavigate?.(block.id, 'up')) e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown' && el && isCaretOnLastLine(el)) {
      if (onNavigate?.(block.id, 'down')) e.preventDefault();
      return;
    }
    if (
      e.key === 'ArrowLeft' &&
      !e.shiftKey &&
      !e.altKey &&
      !e.metaKey &&
      !e.ctrlKey &&
      el &&
      isCaretAtStart(el)
    ) {
      if (onNavigate?.(block.id, 'left')) e.preventDefault();
      return;
    }
    if (
      e.key === 'ArrowRight' &&
      !e.shiftKey &&
      !e.altKey &&
      !e.metaKey &&
      !e.ctrlKey &&
      el &&
      isCaretAtEnd(el)
    ) {
      if (onNavigate?.(block.id, 'right')) e.preventDefault();
      return;
    }

    if (
      shouldHandleLineStartBackspace(e, el, { isEmpty, blockId: block.id }) &&
      consumeListStartBackspace(e, block, {
        content: contentRef.current || '',
        onUpdate,
        onMergeWithPrevious,
      })
    ) {
      return;
    }

    if (tryDeleteAdjacentAtomic(e)) {
      if (el) {
        const value = el.innerHTML;
        contentRef.current = value;
        setContent(value);
        onUpdate(block.id, { content: value });
      }
      return;
    }

    // Delete at end: pull the next block's text up into this item
    if (e.key === 'Delete' && el && isCaretAtEnd(el)) {
      const live = el.innerHTML;
      contentRef.current = live;
      setContent(live);
      if (onMergeWithNext?.(block.id, live)) {
        e.preventDefault();
        return;
      }
    }
  };

  return (
    <>
      <div className="number-block" style={indentStyle(block)}>
        <span className="number-marker" aria-hidden="true">
          {listNumber}.
        </span>
        <ContentEditable
          innerRef={contentEditableRef}
          html={content}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handleEditablePaste}
          className="number-content doc-editable"
          tagName="p"
          placeholder="List item…"
        />
      </div>
      {variablePicker}
      {mathMenu}
    </>
  );
};
